import React from 'react';
import {
  IonCard,
  IonCardContent,
  IonButton,
  IonIcon,
  IonText,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonContent,
} from '@ionic/react';
import { closeOutline, qrCodeOutline, trashOutline } from 'ionicons/icons';
import { LOT_ACCESS_LABEL } from '../../../domain/Lot';
import { useLotShares } from '../../../application/use-cases/lots/useLotShares';
import '../../pages/Lots.css';

// Compartir el lote entero (solo el dueño), por email o con un código QR:
// quien lo recibe ve el lote y sus estaciones; con "ver y editar" además
// cambia el cultivo, las etapas y la siembra.
const LotShareCard: React.FC<{ lotId: string }> = ({ lotId }) => {
  const {
    shares,
    email,
    setEmail,
    level,
    setLevel,
    busy,
    error,
    invite,
    revoke,
    pendingQr,
    qrDataUrl,
    setQrDataUrl,
    qrExpiresAt,
    generateQr,
  } = useLotShares(lotId, true);
  const time = (iso: string) => new Date(iso).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });

  return (
    <IonCard>
      <IonCardContent>
        <p className="lot-section-title">Compartir el lote</p>
        <p className="lot-detail-note">
          Con el lote se comparten sus estaciones. Sirve para que tu agrónomo, un contratista o quien trabaja el campo
          siga el cultivo. Quien lo recibe necesita una cuenta en DomIoT.
        </p>
        {shares.length === 0 && <p className="lot-detail-note">Todavía no lo compartiste con nadie.</p>}
        <ul className="lot-shares">
          {shares.map((share) => (
            <li key={share.id}>
              <span>
                {share.userName}
                <small>
                  {share.userEmail} · {LOT_ACCESS_LABEL[share.level]}
                </small>
              </span>
              <button className="delete-icon-btn" disabled={busy} onClick={() => revoke(share.id)} aria-label={`Quitar a ${share.userName}`}>
                <IonIcon icon={trashOutline} />
              </button>
            </li>
          ))}
        </ul>
        <div className="lot-field-row">
          <label className="lot-field">
            <span>Email de su cuenta</span>
            <input type="email" value={email} placeholder="email@ejemplo.com" onChange={(e) => setEmail(e.target.value)} />
          </label>
          <label className="lot-field">
            <span>Acceso</span>
            <select value={level} onChange={(e) => setLevel(e.target.value as 'view' | 'control')}>
              <option value="view">{LOT_ACCESS_LABEL.view}</option>
              <option value="control">{LOT_ACCESS_LABEL.control}</option>
            </select>
          </label>
        </div>
        {error && (
          <IonText color="danger">
            <p style={{ fontSize: 13 }}>{error}</p>
          </IonText>
        )}
        <IonButton expand="block" fill="outline" disabled={busy || !email.trim()} onClick={invite}>
          Compartir por email
        </IonButton>
        <IonButton expand="block" disabled={busy} onClick={generateQr} style={{ marginTop: 8 }}>
          <IonIcon icon={qrCodeOutline} slot="start" />
          Generar código QR
        </IonButton>
        {pendingQr && (
          <p className="lot-detail-note">
            Hay un código QR activo ({LOT_ACCESS_LABEL[pendingQr.level]}) hasta las {time(pendingQr.expiresAt)}
          </p>
        )}

        <IonModal isOpen={!!qrDataUrl} onDidDismiss={() => setQrDataUrl(null)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Código para compartir el lote</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setQrDataUrl(null)} aria-label="Cerrar">
                  <IonIcon icon={closeOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding ion-text-center">
            {qrDataUrl && <img src={qrDataUrl} alt="Código QR para compartir el lote" style={{ maxWidth: '100%' }} />}
            <IonText color="medium">
              <p>
                Acceso: {LOT_ACCESS_LABEL[level]}. Se usa una sola vez y quien lo escanee necesita una cuenta de DomIoT.
                {qrExpiresAt && ` Vale hasta las ${time(qrExpiresAt)}`}
              </p>
            </IonText>
          </IonContent>
        </IonModal>
      </IonCardContent>
    </IonCard>
  );
};

export default LotShareCard;
